import api from '@/services/api/client';
import {getV2Error, V2ApiError} from '@/services/api/v2';

export type WyscoutImportMonths = 12 | 24 | 36;
export type WyscoutImportStatus = 'queued' | 'running' | 'paused' | 'completed' | 'partial' | 'failed';

type NullableMetric = number | null;
type NullableDecimalMetric = string | number | null;

interface WyscoutImportAggregatesDTO {
  appearances: NullableMetric;
  minutes_played: NullableMetric;
  goals: NullableMetric;
  assists: NullableMetric;
  expected_goals: NullableDecimalMetric;
  expected_assists: NullableDecimalMetric;
}

interface WyscoutImportMatchDTO {
  wyscout_match_id: number;
  date_utc: string;
  competition_id: number;
  label?: string | null;
  player_team_name?: string | null;
  opponent_team_name?: string | null;
  venue?: string | null;
  minutes_played: NullableMetric;
  goals: NullableMetric;
  assists: NullableMetric;
  expected_goals: NullableDecimalMetric;
  expected_assists: NullableDecimalMetric;
}

interface WyscoutImportFailureDTO {
  wyscout_match_id: number;
  code: WyscoutImportFailureCode;
  message: string;
  retryable: boolean;
  ignored: boolean;
}

interface WyscoutPlayerSearchDTO {
  wyscout_player_id: number;
  name: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  position: string;
  team_name: string;
  nationality?: string;
  image_url: string;
  competition_ids: number[];
  season_ids: number[];
  existing_player_id?: string;
}

interface WyscoutImportRunDTO {
  id: string;
  workspace_id: string;
  player_id: string;
  wyscout_player_id: number;
  requested_months: WyscoutImportMonths;
  cutoff_at: string;
  status: WyscoutImportStatus;
  discovered_at?: string;
  selected_count: number;
  succeeded_count: number;
  failed_count: number;
  current_match_id?: number;
  failure_summary?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  can_retry?: boolean;
  retained_data?: boolean;
  aggregates?: WyscoutImportAggregatesDTO | null;
  matches?: WyscoutImportMatchDTO[] | null;
  failures?: WyscoutImportFailureDTO[] | null;
}

export interface WyscoutPlayerSearchResult {
  wyscoutPlayerId: number;
  name: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  position: string;
  teamName: string;
  nationality: string;
  imageUrl: string;
  competitionIds: number[];
  seasonIds: number[];
  existingPlayerId?: string;
}

export interface WyscoutPlayerSearchResponse {
  data: WyscoutPlayerSearchResult[];
  warnings: string[];
}

export interface WyscoutImportRun {
  id: string;
  workspaceId: string;
  playerId: string;
  wyscoutPlayerId: number;
  requestedMonths: WyscoutImportMonths;
  cutoffAt: string;
  status: WyscoutImportStatus;
  discoveredAt?: string;
  selectedCount: number;
  succeededCount: number;
  failedCount: number;
  currentMatchId?: number;
  failureSummary?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  reused: boolean;
  canRetry: boolean;
  retainedData: boolean;
  aggregates: WyscoutImportAggregates | null;
  matches: WyscoutImportMatch[];
  failures: WyscoutImportFailure[];
  matchPage?: WyscoutImportMatchPage;
}

export type WyscoutImportFailureCode =
  | 'no_statistics'
  | 'rate_limited'
  | 'provider_unavailable'
  | 'timeout'
  | 'authentication_failed'
  | 'invalid_data'
  | 'unknown';

export interface WyscoutImportFailure {
  wyscoutMatchId: number;
  code: WyscoutImportFailureCode;
  message: string;
  retryable: boolean;
  ignored: boolean;
}

export interface WyscoutImportMatchPage {
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface WyscoutImportAggregates {
  appearances: NullableMetric;
  minutesPlayed: NullableMetric;
  goals: NullableMetric;
  assists: NullableMetric;
  expectedGoals: NullableDecimalMetric;
  expectedAssists: NullableDecimalMetric;
}

export interface WyscoutImportMatch {
  wyscoutMatchId: number;
  dateUtc: string;
  competitionId: number;
  label: string | null;
  playerTeamName: string | null;
  opponentTeamName: string | null;
  venue: string | null;
  minutesPlayed: NullableMetric;
  goals: NullableMetric;
  assists: NullableMetric;
  expectedGoals: NullableDecimalMetric;
  expectedAssists: NullableDecimalMetric;
}

const headers = (workspaceId: string) => ({'X-Workspace-ID': workspaceId});

const safeWyscoutImageURL = (value: string): string => {
  try {
    const parsed = new URL(value);
    const hostname = parsed.hostname.toLowerCase();
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port) return '';
    return hostname === 'wyscout.com' || hostname.endsWith('.wyscout.com') ? parsed.toString() : '';
  } catch {
    return '';
  }
};

export const routeablePlayerConflict = (details: unknown): {player_id: string; scope: 'squad' | 'external'} | null => {
  if (!details || typeof details !== 'object') return null;
  const playerId = (details as Record<string, unknown>).player_id;
  const scope = (details as Record<string, unknown>).scope;
  if (typeof playerId !== 'string'
    || playerId === '00000000-0000-0000-0000-000000000000'
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(playerId)
    || (scope !== 'squad' && scope !== 'external')) return null;
  return {player_id: playerId, scope};
};

const mapError = (error: unknown): V2ApiError => {
  const response = (error as {response?: {data?: {error?: {code?: string}; details?: unknown; request_id?: unknown}}})?.response?.data;
  if (response?.error?.code === 'player_already_imported') {
    const details = routeablePlayerConflict(response.details);
    return {
      code: 'player_already_imported',
      message: 'This WyScout identity is already linked to another player.',
      details: details ?? {},
      requestId: typeof response.request_id === 'string' ? response.request_id : '',
    };
  }
  return getV2Error(error);
};

const mapPlayer = (player: WyscoutPlayerSearchDTO): WyscoutPlayerSearchResult => ({
  wyscoutPlayerId: player.wyscout_player_id,
  name: player.name,
  firstName: player.first_name,
  lastName: player.last_name,
  birthDate: player.birth_date,
  position: player.position,
  teamName: player.team_name,
  nationality: player.nationality?.trim() ?? '',
  imageUrl: safeWyscoutImageURL(player.image_url),
  competitionIds: player.competition_ids,
  seasonIds: player.season_ids,
  existingPlayerId: player.existing_player_id,
});

const mapAggregates = (value?: WyscoutImportAggregatesDTO | null): WyscoutImportAggregates | null => value ? ({
  appearances: value.appearances,
  minutesPlayed: value.minutes_played,
  goals: value.goals,
  assists: value.assists,
  expectedGoals: value.expected_goals,
  expectedAssists: value.expected_assists,
}) : null;

const mapMatch = (value: WyscoutImportMatchDTO): WyscoutImportMatch => ({
  wyscoutMatchId: value.wyscout_match_id,
  dateUtc: value.date_utc,
  competitionId: value.competition_id,
  label: value.label ?? null,
  playerTeamName: value.player_team_name ?? null,
  opponentTeamName: value.opponent_team_name ?? null,
  venue: value.venue ?? null,
  minutesPlayed: value.minutes_played,
  goals: value.goals,
  assists: value.assists,
  expectedGoals: value.expected_goals,
  expectedAssists: value.expected_assists,
});

const mapFailure = (value: WyscoutImportFailureDTO): WyscoutImportFailure => ({
  wyscoutMatchId: value.wyscout_match_id,
  code: value.code,
  message: value.message,
  retryable: value.retryable,
  ignored: value.ignored,
});

const mapRun = (run: WyscoutImportRunDTO, reused: boolean): WyscoutImportRun => ({
  id: run.id,
  workspaceId: run.workspace_id,
  playerId: run.player_id,
  wyscoutPlayerId: run.wyscout_player_id,
  requestedMonths: run.requested_months,
  cutoffAt: run.cutoff_at,
  status: run.status,
  discoveredAt: run.discovered_at,
  selectedCount: run.selected_count,
  succeededCount: run.succeeded_count,
  failedCount: run.failed_count,
  currentMatchId: run.current_match_id,
  failureSummary: run.failure_summary,
  createdAt: run.created_at,
  updatedAt: run.updated_at,
  completedAt: run.completed_at,
  reused,
  canRetry: run.can_retry === true,
  retainedData: run.retained_data === true,
  aggregates: mapAggregates(run.aggregates),
  matches: Array.isArray(run.matches) ? run.matches.map(mapMatch) : [],
  failures: Array.isArray(run.failures) ? run.failures.map(mapFailure) : [],
});

export const wyscoutImportAPI = {
  search: async (
    workspaceId: string,
    query: string,
    months: WyscoutImportMonths,
    signal: AbortSignal,
  ): Promise<WyscoutPlayerSearchResponse> => {
    try {
      const response = await api.get<{data: WyscoutPlayerSearchDTO[]; warnings: string[]}>(
        '/v2/wyscout/player-search',
        {params: {q: query, months}, headers: headers(workspaceId), signal},
      );
      return {data: response.data.data.map(mapPlayer), warnings: response.data.warnings};
    } catch (error) {
      throw mapError(error);
    }
  },
  create: async (
    workspaceId: string,
    wyscoutPlayerId: number,
    months: WyscoutImportMonths,
  ): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO; reused: boolean}>(
        '/v2/wyscout/player-imports',
        {wyscout_player_id: wyscoutPlayerId, months},
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, response.data.reused);
    } catch (error) {
      throw mapError(error);
    }
  },
  startLinked: async (
    workspaceId: string,
    playerId: string,
    months: WyscoutImportMonths,
  ): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO; reused: boolean}>(
        `/v2/players/${playerId}/wyscout-imports`,
        {months},
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, response.data.reused);
    } catch (error) {
      throw mapError(error);
    }
  },
  link: async (
    workspaceId: string,
    playerId: string,
    wyscoutPlayerId: number,
    months: WyscoutImportMonths,
  ): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO; reused: boolean}>(
        `/v2/players/${playerId}/wyscout-link-imports`,
        {wyscout_player_id: wyscoutPlayerId, months},
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, response.data.reused);
    } catch (error) {
      throw mapError(error);
    }
  },
  latest: async (workspaceId: string, playerId: string, page = 1): Promise<WyscoutImportRun> => {
    try {
      const response = await api.get<{data: WyscoutImportRunDTO; meta: {page: number; page_size: number; has_more: boolean}}>(
        `/v2/players/${playerId}/wyscout-import`,
        {headers: headers(workspaceId), ...(page > 1 ? {params: {page, page_size: 100}} : {})},
      );
      return {
        ...mapRun(response.data.data, false),
        matchPage: {
          page: response.data.meta.page,
          pageSize: response.data.meta.page_size,
          hasMore: response.data.meta.has_more,
        },
      };
    } catch (error) {
      throw mapError(error);
    }
  },
  retry: async (workspaceId: string, runId: string): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO}>(
        `/v2/wyscout/player-imports/${runId}/retries`,
        undefined,
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, false);
    } catch (error) {
      throw mapError(error);
    }
  },
  pause: async (workspaceId: string, runId: string): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO}>(
        `/v2/wyscout/player-imports/${runId}/pause`,
        undefined,
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, false);
    } catch (error) {
      throw mapError(error);
    }
  },
  resume: async (workspaceId: string, runId: string): Promise<WyscoutImportRun> => {
    try {
      const response = await api.post<{data: WyscoutImportRunDTO}>(
        `/v2/wyscout/player-imports/${runId}/resume`,
        undefined,
        {headers: headers(workspaceId)},
      );
      return mapRun(response.data.data, false);
    } catch (error) {
      throw mapError(error);
    }
  },
  ignoreMatch: async (workspaceId: string, playerId: string, wyscoutMatchId: number): Promise<void> => {
    try {
      await api.put(
        `/v2/players/${playerId}/wyscout-import/ignored-matches/${wyscoutMatchId}`,
        undefined,
        {headers: headers(workspaceId)},
      );
    } catch (error) {
      throw mapError(error);
    }
  },
  includeMatch: async (workspaceId: string, playerId: string, wyscoutMatchId: number): Promise<void> => {
    try {
      await api.delete(
        `/v2/players/${playerId}/wyscout-import/ignored-matches/${wyscoutMatchId}`,
        {headers: headers(workspaceId)},
      );
    } catch (error) {
      throw mapError(error);
    }
  },
};
